import { PATIENT_BANNER_RESET } from '../PatientFolder/PatientBanner/actions'

import {
  ENCOUNTER_START,
  ENCOUNTER_START_ERROR,
  ENCOUNTER_STARTED,
  ENCOUNTER_SEARCH,
  ENCOUNTER_FOUND,
  ENCOUNTER_FOUND_ERROR,
  ENCOUNTER_SIGN,
  ENCOUNTER_SIGNED,
  ENCOUNTER_SIGN_ERROR,
  ENCOUNTER_RESET,
  ENCOUNTER_GET_CHANGES,
  ENCOUNTER_GET_CHANGES_SUCCESS,
  ENCOUNTER_GET,
  ENCOUNTER_GOT,
  GRAND_ROUNDS_GET_SUMMARY_SUCCESS,
  GRAND_ROUNDS_GET_SUMMARY,
  ENCOUNTER_START_TIMER,
  ENCOUNTER_STOP_TIMER,
  ENCOUNTER_DURATION,
  ENCOUNTER_DURATION_ERROR
} from './actions'

interface Encounter {
  encounter?: {
    id?: string
  }
  id?: string
  _signing?: boolean
  error?: Error
  statusHistory?: Record<string, unknown>
  status?: Record<string, unknown>
  timerLoading?: boolean
  author_id?: string
  document?: {
    type?: string
  }
}

interface State {
  timerLoading: boolean
  fetched: boolean
  encounterSummaryFetched: boolean
  encounterSummaryLoading: boolean
  grandRoundsLoading: boolean
  grandRoundsReportData?: Record<string, unknown>
  encounterSummaryData?: string
  encounterSummaryHtml?: string
  _loading?: boolean
  encounter?: Encounter
  otherEncounters?: Encounter[]
  time?: number
  error?: Error
  signed?: boolean
  timerError?: Error
}

const initialState: State = {
  timerLoading: false,
  fetched: false,
  encounterSummaryFetched: false,
  encounterSummaryLoading: false,
  grandRoundsLoading: false
}

type Action =
  | GrandRoundsGetSummary
  | GrandRoundsGetSummarySuccess
  | EncounterGetChanges
  | EncounterGetChangesSuccess
  | EncounterStart
  | EncounterStarted
  | EncounterStartError
  | EncounterSign
  | EncounterSearch
  | EncounterSignError
  | EncounterSigned
  | EncounterReset
  | PatientBannerReset
  | EncounterFound
  | EncounterFoundError
  | EncounterGet
  | EncounterGot
  | EncounterDuration
  | EncounterDurationError
  | EncounterStartTimer
  | EncounterStopTimer

interface GrandRoundsGetSummary {
  type: 'GRAND_ROUNDS_GET_SUMMARY'
}

interface GrandRoundsGetSummarySuccess {
  type: 'GRAND_ROUNDS_GET_SUMMARY_SUCCESS'
  reportData: Record<string, unknown>
}

interface EncounterGetChanges {
  type: 'ENCOUNTER_GET_CHANGES'
}

interface EncounterGetChangesSuccess {
  type: 'ENCOUNTER_GET_CHANGES_SUCCESS'
  reportData: string
  reportHtml: string
}

interface EncounterStart {
  type: 'ENCOUNTER_START'
}

interface EncounterStarted {
  type: 'ENCOUNTER_STARTED'
  encounter: Encounter
}

interface EncounterStartError {
  type: 'ENCOUNTER_START_ERROR'
  error: Error
}

interface EncounterSign {
  type: 'ENCOUNTER_SIGN'
}

interface EncounterSearch {
  type: 'ENCOUNTER_SEARCH'
}

interface EncounterSignError {
  type: 'ENCOUNTER_SIGN_ERROR'
  error: Error
}

interface EncounterSigned {
  type: 'ENCOUNTER_SIGNED'
  encounter: Encounter
}

interface EncounterReset {
  type: 'ENCOUNTER_RESET'
}

interface PatientBannerReset {
  type: 'PATIENT_BANNER_RESET'
}

interface EncounterFound {
  type: 'ENCOUNTER_FOUND'
  encounter: Encounter
  otherEncounters: Encounter[]
}
interface EncounterFoundError {
  type: 'ENCOUNTER_FOUND_ERROR'
  error: Error
}

interface EncounterGet {
  type: 'ENCOUNTER_GET'
}

interface EncounterGot {
  type: 'ENCOUNTER_GOT'
  encounter: Encounter
}

interface EncounterDuration {
  type: 'ENCOUNTER_DURATION'
}

interface EncounterDurationError {
  type: 'ENCOUNTER_DURATION_ERROR'
  error: Error
}
interface EncounterStartTimer {
  type: 'ENCOUNTER_START_TIMER'
}

interface EncounterStopTimer {
  type: 'ENCOUNTER_STOP_TIMER'
}

export default function reducer (state: State = initialState, action: Action): State {
  switch (action.type) {
    case GRAND_ROUNDS_GET_SUMMARY:
      return {
        ...state,
        grandRoundsLoading: true
      }
    case GRAND_ROUNDS_GET_SUMMARY_SUCCESS:
      return {
        ...state,
        grandRoundsLoading: false,
        grandRoundsReportData: action.reportData
      }
    case ENCOUNTER_GET_CHANGES:
      return {
        ...state,
        encounterSummaryLoading: true
      }
    case ENCOUNTER_GET_CHANGES_SUCCESS:
      return {
        ...state,
        encounterSummaryLoading: false,
        encounterSummaryData: action.reportData,
        encounterSummaryHtml: action.reportHtml,
        encounterSummaryFetched: true
      }
    case ENCOUNTER_START:
      return {
        ...state,
        _loading: true
      }
    case ENCOUNTER_STARTED:
      return {
        ...state,
        encounter: action.encounter,
        _loading: false,
        time: 0
      }
    case ENCOUNTER_START_ERROR:
      return {
        ...state,
        error: action.error,
        _loading: false,
        fetched: true
      }
    case ENCOUNTER_SIGN:
      if (!state.encounter) {
        return state
      }

      return {
        ...state,
        encounter: {
          ...state.encounter,
          _signing: true
        }
      }
    case ENCOUNTER_SEARCH:
      return {
        ...state,
        timerLoading: true,
        _loading: true
      }
    case ENCOUNTER_SIGN_ERROR:
      return {
        ...state,
        encounter: {
          ...state?.encounter ?? {},
          _signing: false,
          error: action.error
        }
      }
    case ENCOUNTER_SIGNED:
      return {
        ...state,
        encounter: {
          ...state.encounter,
          encounter: action.encounter,
          _signing: false
        },
        signed: true
      }
    case ENCOUNTER_RESET:
      return { ...initialState, otherEncounters: state.otherEncounters }
    case PATIENT_BANNER_RESET:
      return initialState
    case ENCOUNTER_FOUND:
      return {
        ...state,
        _loading: false,
        encounter: action.encounter,
        otherEncounters: action.otherEncounters,
        timerLoading: false,
        fetched: true
      }
    case ENCOUNTER_FOUND_ERROR:
      return {
        ...state,
        _loading: false,
        error: { ...action.error },
        timerLoading: false,
        fetched: true
      }
    case ENCOUNTER_GET:
      return {
        ...state,
        _loading: true
      }
    case ENCOUNTER_GOT:
      const { statusHistory, status } = action.encounter
      if (!state.encounter) {
        return state
      }

      const newEncounter = Object.assign(state.encounter, {
        statusHistory,
        status
      })
      return {
        ...state,
        encounter: newEncounter,
        timerLoading: false
      }

    case ENCOUNTER_DURATION:
      return {
        ...state,
        timerLoading: true
      }
    case ENCOUNTER_DURATION_ERROR:
      return {
        ...state,
        timerLoading: false,
        timerError: action.error
      }
    case ENCOUNTER_START_TIMER:
      return { ...state }
    case ENCOUNTER_STOP_TIMER:
      return { ...state }
    default:
      return state
  }
}
